import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { readAuthToken } from '@noor/api';
import { env } from '@noor/config';
import {
  buildSlotListFilter,
  slotListSearchSchema,
  slotQueries,
  type SlotListSearch,
} from '@/entities/bonus/slot';
import { SlotPage } from '@/pages/bonus/slot';

/**
 * Bonus slots — cpanel `/slot`. The URL is the single source of truth for the
 * page, the open dialog (`modalType` + `slotId`) and the reserves window.
 * Prefetch is best-effort and non-blocking; the table surfaces its own loading
 * state. Gated by the `slots.view` permission.
 */
export const Route = createFileRoute('/slot')({
  staticData: { permission: 'slots.view' },
  beforeLoad: () => {
    // Auth guard: against the real API a token is required. In MSW dev mode the
    // mock backend authenticates everything, so the guard only bites with MSW off.
    if (!env.VITE_ENABLE_MSW && !readAuthToken()) {
      // `redirect()` is TanStack Router's control-flow throw, not an Error.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login' });
    }
  },
  validateSearch: slotListSearchSchema,
  loaderDeps: ({ search }) => ({ page: search.page, pageSize: search.pageSize }),
  loader: ({ context, deps }) => {
    void context.queryClient
      .prefetchQuery(
        slotQueries.list(buildSlotListFilter(slotListSearchSchema.parse(deps))),
      )
      .catch(() => undefined);
  },
  component: SlotRoute,
});

function SlotRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const onParamsChange = (next: Partial<SlotListSearch>) => {
    void navigate({ search: (prev) => ({ ...prev, ...next }) });
  };

  return <SlotPage params={search} onParamsChange={onParamsChange} />;
}
